import { yupResolver } from '@hookform/resolvers/yup';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Card,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { ChangePassWordSchema } from '../../../schemas';
import { userLogout } from '../../../shared/auth/authService';
import { changePassword } from '../store/authActions';

export default function AccountChangePassword() {
  const dispatch = useDispatch();
  const { isLoading, isSuccess } = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const defaultValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const methods = useForm({
    resolver: yupResolver(ChangePassWordSchema),
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    // reset,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const [showPasswords, setShowPasswords] = useState(false);

  // Real-time password checks (used to render strength meter and hints)
  const newPasswordValue = watch('newPassword') || '';

  const passwordChecks = {
    length: newPasswordValue.length >= 8,
    lower: /[a-z]/.test(newPasswordValue),
    upper: /[A-Z]/.test(newPasswordValue),
    digit: /\d/.test(newPasswordValue),
    special: /[^A-Za-z0-9]/.test(newPasswordValue),
  };

  const passedCount = Object.values(passwordChecks).filter(Boolean).length;
  const strength = Math.round((passedCount / 5) * 100);

  const strengthLabel =
    passedCount <= 1
      ? 'Very Weak'
      : passedCount === 2
      ? 'Weak'
      : passedCount === 3
      ? 'Fair'
      : passedCount === 4
      ? 'Good'
      : 'Strong';

  const onSubmit = async (data) => {
    const payload = {
      old_password: data.oldPassword,
      new_password: data.newPassword,
    };
    dispatch(changePassword(payload));
  };

  useEffect(() => {
    if (isSuccess) {
      userLogout();
    }
  }, [isSuccess, navigate]);

  return (
    <Card sx={{ p: 3 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} alignItems="flex-end">
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              edge="end"
              onClick={() => setShowPasswords((s) => !s)}
              aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
            >
              {showPasswords ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Box>

          <RHFTextField name="oldPassword" type={showPasswords ? 'text' : 'password'} label="Old Password" />

          <RHFTextField
            name="newPassword"
            type={showPasswords ? 'text' : 'password'}
            label="New Password"
            autoComplete="new-password"
          />

          <Box sx={{ width: '100%', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Password requirements:
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              At least 8 characters, including uppercase, lowercase, number and special character
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={strength} sx={{ height: 8, borderRadius: 1 }} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  {strengthLabel}
                </Typography>
              </Box>
            </Box>

            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.length ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="At least 8 characters" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.upper ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One uppercase letter (A-Z)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.lower ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One lowercase letter (a-z)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.digit ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One number (0-9)" />
              </ListItem>

              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {passwordChecks.special ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary="One special character (e.g. !@#$%)" />
              </ListItem>
            </List>
          </Box>

          <RHFTextField
            name="confirmNewPassword"
            type={showPasswords ? 'text' : 'password'}
            label="Confirm New Password"
          />

          <LoadingButton type="submit" variant="contained" size="small" disabled={isSubmitting} loading={isLoading}>
            Change Password
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Card>
  );
}
